/**
 * 仅顶部导航 `SystemSelect`：按业务领域分组 options + 分组场景下的搜索。
 * 其他页面请用 `@/utils/systemSelectOptions` 的 `buildSystemSelectOptions`。
 */

import type { SystemListItem } from './systemSelectOptions';

/** 与 `SystemListItem` 同形，供导航栏类型标注 */
export type SystemListItemForGroup = SystemListItem;

const OTHER_GROUP_LABEL = '其他';

function isAllSystemItem(item: SystemListItem): boolean {
  return String(item.systemId ?? '').toLowerCase() === 'all';
}

export type BuildGroupedSystemSelectOptionsParams = {
  /** 不包含「全部」项（systemId === 'all'），用于表单必选系统等场景 */
  excludeAll?: boolean;
};

/**
 * 将接口返回的系统列表转为分组 Select options。
 * - 若存在 systemId 为 `all` 的项：作为首项置顶，其余按 businessName 分组
 * - 若无 all：仅分组
 * - excludeAll: true 时去掉 all 项后再分组
 */
export function buildGroupedSystemSelectOptions(
  systemList: SystemListItem[] | undefined,
  params?: BuildGroupedSystemSelectOptionsParams,
): any[] {
  let list = systemList;
  if (!list?.length) return [];
  if (params?.excludeAll) {
    list = list.filter((x) => !isAllSystemItem(x));
  }
  const allItem = list.find(isAllSystemItem);
  const listForGrouping = allItem ? list.filter((x) => !isAllSystemItem(x)) : list;

  const byBusiness = new Map<string, SystemListItem[]>();
  for (const item of listForGrouping) {
    const raw = item.businessName != null ? String(item.businessName).trim() : '';
    const key = raw || OTHER_GROUP_LABEL;
    if (!byBusiness.has(key)) byBusiness.set(key, []);
    byBusiness.get(key)!.push(item);
  }
  const sortedKeys = [...byBusiness.keys()].sort((a, b) => {
    if (a === OTHER_GROUP_LABEL) return 1;
    if (b === OTHER_GROUP_LABEL) return -1;
    return a.localeCompare(b, 'zh-CN');
  });
  const groups = sortedKeys.map((groupLabel) => ({
    label: groupLabel,
    options: (byBusiness.get(groupLabel) || [])
      .slice()
      .sort((a, b) => (a.systemName || '').localeCompare(b.systemName || '', 'zh-CN'))
      .map((item) => {
        const businessLabel = item.businessName != null ? String(item.businessName).trim() : '';
        return {
          label: item.systemName,
          value: item.systemId,
          businessName: businessLabel || groupLabel,
          ...item,
        };
      }),
  }));

  if (!allItem) return groups;

  const allBusinessLabel = allItem.businessName != null ? String(allItem.businessName).trim() : '';
  const allOption = {
    label: allItem.systemName,
    value: allItem.systemId,
    businessName: allBusinessLabel || 'ALL',
    ...allItem,
  };

  return [allOption, ...groups];
}

/** 分组系统下拉的搜索：匹配系统名或业务领域名 */
export function filterGroupedSystemSelectOption(input: string, option?: any): boolean {
  const q = (input || '').trim().toLowerCase();
  if (!q) return true;
  if (!option) return false;
  if (Array.isArray(option.options)) {
    return String(option.label ?? '').toLowerCase().includes(q);
  }
  const label = String(option.label ?? '').toLowerCase();
  const businessName = String(option.businessName ?? '').toLowerCase();
  return label.includes(q) || businessName.includes(q);
}
